import { Type_RefFuncKrpano } from '@components/Krpano/types';
import { useTourEventState } from '@containers/Home/global/TourStats';
import { useKrpano } from '@containers/Home/hook/useKrpano';
import { LANGUAGES } from '@containers/Home/language';
import {
  selectConfigSoundProject,
  selectLanguageProject,
} from '@containers/Home/selectors';
import { Popover, Tooltip } from 'antd';
import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import { ControlbarPopoverWrapper } from './styled';

interface Props {
  krpanoRef: Type_RefFuncKrpano;
  isShowListScene: boolean;
  isShowControlbar: boolean;
  onToggleControlbar: () => void;
  onToggleListScene: () => void;
  onPrevScene: () => void;
  onNextScene: () => void;
}

const ControlbarAction = memo(
  ({
    krpanoRef,
    onPrevScene,
    onNextScene,
    isShowListScene,
    isShowControlbar,
    onToggleControlbar,
    onToggleListScene,
  }: Props) => {
    const [{ isSoundOnScene, isActiveModeMultipleStaging }] =
      useTourEventState('state');
    const languageProject = useSelector(selectLanguageProject);
    const configSoundProject = useSelector(selectConfigSoundProject);

    const {
      //
      onVR,
      onToggleGyro,
      onFullscreen,
      onToFirstScene,
      onViewNormal,
      onViewMirrorBall,
      onViewPlannet,
      // NOTE: auto change scene
      isChangeScene,
      isShowIconChangeScene,
      onChangeAutoChangeScene,
      // NOTE: auto rotate
      isRotate,
      isShowIconRotate,
      onToggleRotate,
      // NOTE: sound scene
      isShowIconSoundScene,
      onToggleSoundScene,
      // NOTE: sound project
      isShowIconSoundProject,
      onTurnOffSoundProject,
      onTurnOnSoundProject,
      // NOTE: multiple staging
      isShowIconMultipleStaging,
      onToggleModeMultipleStaging,
    } = useKrpano({ krpanoRef });

    return (
      <div className="controlBar">
        <div className="--left">
          <button
            type="button"
            className="itemControlBtn"
            onClick={onPrevScene}
          >
            <i aria-hidden className="far fa-chevron-circle-left" />
          </button>
          <button
            type="button"
            className={`itemControlBtn ${isShowListScene ? 'active' : ''}`}
            onClick={onToggleListScene}
          >
            <i aria-hidden className="far fa-th" />
          </button>
        </div>
        <div className="--center">
          <Tooltip
            title={LANGUAGES[languageProject].controlbarFirstScene}
            placement="top"
          >
            <button
              type="button"
              className="itemControlBtn"
              onClick={onToFirstScene}
            >
              <i aria-hidden className="far fa-home" />
            </button>
          </Tooltip>
          {isShowIconSoundProject && (
            <Tooltip
              title={
                configSoundProject.on
                  ? LANGUAGES[languageProject].controlbarTurnOffSoundProject
                  : LANGUAGES[languageProject].controlbarTurnOnSoundProject
              }
              placement="top"
            >
              <button
                type="button"
                className="itemControlBtn"
                onClick={
                  configSoundProject.on
                    ? onTurnOffSoundProject
                    : onTurnOnSoundProject
                }
              >
                <i
                  className={`far fa-${
                    configSoundProject.on ? 'volume' : 'volume-mute'
                  }`}
                />
              </button>
            </Tooltip>
          )}
          {(isShowIconSoundScene ||
            isShowIconRotate ||
            isShowIconChangeScene) && (
            <Popover
              placement="top"
              zIndex={99}
              content={
                <ControlbarPopoverWrapper>
                  <ul>
                    {isShowIconSoundScene && (
                      <li>
                        <button type="button" onClick={onToggleSoundScene}>
                          <i
                            className={`far fa-${
                              !isSoundOnScene ? 'volume-off' : 'volume-up'
                            }`}
                          />
                          {LANGUAGES[languageProject].controlbarSoundScene}
                          {/* {isSoundOnScene ? 'Sound Scene' : 'Sound Scene'} */}
                        </button>
                      </li>
                    )}
                    {isShowIconRotate && (
                      <li>
                        <button type="button" onClick={onToggleRotate}>
                          <i
                            className={
                              isRotate ? `far fa-repeat` : `far fa-repeat`
                            }
                          />
                          {isRotate
                            ? LANGUAGES[languageProject].controlbarStopRotate
                            : LANGUAGES[languageProject].controlbarStartRotate}
                        </button>
                      </li>
                    )}
                    {isShowIconChangeScene && (
                      <li>
                        <button
                          type="button"
                          onClick={onChangeAutoChangeScene.bind(
                            this,
                            !isChangeScene,
                          )}
                        >
                          <i
                            className={`far fa-${
                              isChangeScene ? 'pause' : 'play'
                            }`}
                          />
                          {isChangeScene
                            ? LANGUAGES[languageProject]
                                .controlbarStopAutoPlayScene
                            : LANGUAGES[languageProject]
                                .controlbarStartAutoPlayScene}
                        </button>
                      </li>
                    )}
                  </ul>
                </ControlbarPopoverWrapper>
              }
              overlayClassName="popoverControlbar"
            >
              <button type="button" className="itemControlBtn">
                <i aria-hidden className="far fa-play" />
              </button>
            </Popover>
          )}
          <Popover
            placement="top"
            zIndex={99}
            content={
              <ControlbarPopoverWrapper>
                <ul>
                  <li>
                    <button type="button" onClick={() => onViewNormal()}>
                      <i aria-hidden className="far fa-mountains" />
                      Normal
                    </button>
                  </li>
                  <li>
                    <button type="button" onClick={() => onViewMirrorBall()}>
                      <i aria-hidden className="far fa-globe" />
                      Mirror Ball
                    </button>
                  </li>
                  <li>
                    <button type="button" onClick={() => onViewPlannet()}>
                      <i aria-hidden className="far fa-globe-asia" />
                      Little Planel
                    </button>
                  </li>
                </ul>
              </ControlbarPopoverWrapper>
            }
            overlayClassName="popoverControlbar"
          >
            <button type="button" className="itemControlBtn">
              <i aria-hidden className="far fa-eye" />
            </button>
          </Popover>

          {isShowIconMultipleStaging && (
            <Tooltip
              title={
                isActiveModeMultipleStaging
                  ? LANGUAGES[languageProject]
                      .controlbarTurnOffModeMultipleStaging
                  : LANGUAGES[languageProject]
                      .controlbarTurnOnModeMultipleStaging
              }
              placement="top"
            >
              <button
                type="button"
                className="itemControlBtn hide-on-mobile"
                onClick={() => onToggleModeMultipleStaging()}
              >
                <i aria-hidden className="far fa-images" />
              </button>
            </Tooltip>
          )}

          <button
            type="button"
            className="itemControlBtn show-on-mobile"
            onClick={onToggleGyro}
          >
            <i aria-hidden className="far fa-hurricane" />
          </button>

          <Tooltip
            title={LANGUAGES[languageProject].controlbarVRMode}
            placement="top"
          >
            <button type="button" className="itemControlBtn" onClick={onVR}>
              <i aria-hidden className="far fa-head-vr" />
            </button>
          </Tooltip>

          <Tooltip
            title={LANGUAGES[languageProject].controlbarFullscreen}
            placement="top"
          >
            <button
              type="button"
              className="itemControlBtn"
              onClick={onFullscreen}
            >
              <i aria-hidden className="far fa-expand-arrows-alt" />
            </button>
          </Tooltip>
        </div>
        <div className="--right">
          <button
            type="button"
            className="itemControlBtn"
            onClick={onToggleControlbar}
          >
            <i aria-hidden className="far fa-caret-circle-down" />
          </button>
          <button
            type="button"
            className="itemControlBtn"
            onClick={onNextScene}
          >
            <i aria-hidden className="far fa-chevron-circle-right" />
          </button>
        </div>

        <button
          type="button"
          className={`itemControlBtn itemShowControlbar ${
            !isShowControlbar ? '--show' : '--hide'
          }`}
          onClick={onToggleControlbar}
        >
          <i aria-hidden className="far fa-caret-circle-up" />
        </button>
      </div>
    );
  },
);

export default ControlbarAction;
